/**
 * Payment Mode Table Component
 * Displays transaction history grouped by Payment Mode with rowspan grouping
 */
import React, { Component } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

export class PaymentModeTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            transactions: [],
            flattenedData: [],
            isLoading: false
        };

        // Create refs for scroll synchronization
        this.dataScrollRef = React.createRef();
        this.grandTotalScrollRef = React.createRef();

        // Bind methods
        this.syncScroll = this.syncScroll.bind(this);
        this.processTransactionsData = this.processTransactionsData.bind(this);
    }

    componentDidMount() {
        // Check if transactionsData is already passed as prop
        const { transactionsData, transactionsLoading } = this.props;

        if (transactionsData) {
            // Use data from props
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false) {
            // Data already loaded but empty
            this.setState({ isLoading: false, transactions: [], flattenedData: [] });
        }
    }

    processTransactionsData(data) {
        console.log('PaymentModeTable - Processing transactions data:', data);
        
        let collections = data?.collections || [];

        if (collections && collections.length > 0) {
            const transformedData = this.transformApiDataToTableFormat(collections);
            console.log('PaymentModeTable - Transformed Data:', transformedData);
            
            const flattened = this.flattenTransactions(transformedData);
            console.log('PaymentModeTable - Flattened Data:', flattened);
            
            this.setState({
                transactions: transformedData,
                flattenedData: flattened,
                isLoading: false
            }, () => {
                // Attach scroll listener after data loads
                setTimeout(() => {
                    this.attachScrollListener();
                }, 100);
            });
        } else {
            console.log('PaymentModeTable - No collections in data');
            this.setState({ 
                transactions: [],
                flattenedData: [],
                isLoading: false 
            });
        }
    }

    componentDidUpdate(prevProps) {
        // Check if transactionsData prop has changed
        const { transactionsData, transactionsLoading } = this.props;
        
        if (transactionsData !== prevProps.transactionsData && transactionsData) {
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false && prevProps.transactionsLoading === true && !transactionsData) {
            // Loading finished but no data
            this.setState({ isLoading: false, transactions: [], flattenedData: [] });
        }
    }

    componentWillUnmount() {
        // Clean up scroll event listener
        const grandTotalWrapper = this.grandTotalScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (grandTotalWrapper) {
            grandTotalWrapper.removeEventListener('scroll', this.syncScroll);
        }
    }

    attachScrollListener = () => {
        const grandTotalWrapper = this.grandTotalScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (grandTotalWrapper) {
            // Remove existing listener first to avoid duplicates
            grandTotalWrapper.removeEventListener('scroll', this.syncScroll);
            // Add new listener
            grandTotalWrapper.addEventListener('scroll', this.syncScroll);
            console.log('Scroll listener attached to grand total');
        }
    }

    syncScroll(e) {
        const scrollLeft = e.target.scrollLeft;
        const dataWrapper = this.dataScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (dataWrapper) {
            dataWrapper.scrollLeft = scrollLeft;
            console.log('Syncing scroll - scrollLeft:', scrollLeft);
        }
    }

    flattenTransactions = (paymentModeGroups) => {
        const flattened = [];
        let globalSNo = 1;
        
        paymentModeGroups.forEach((group, groupIndex) => {
            let groupGlobalIndex = 0;
            
            // Calculate total rows in this payment mode group
            let totalRowsInGroup = 0;
            const transactionGroups = {};
            
            // Group by receipt number (unique identifier for each transaction)
            group.feeDetails.forEach(feeDetail => {
                const receiptNo = feeDetail.receiptNo;
                if (!transactionGroups[receiptNo]) {
                    transactionGroups[receiptNo] = [];
                }
                transactionGroups[receiptNo].push(feeDetail);
            });
            
            // Count total rows
            Object.keys(transactionGroups).forEach(receiptNo => {
                totalRowsInGroup += transactionGroups[receiptNo].length;
            });

            // Flatten each transaction group
            Object.keys(transactionGroups).forEach((receiptNo, txnIndex) => {
                const txnFeeDetails = transactionGroups[receiptNo];
                const totalFeeDetails = txnFeeDetails.length;
                
                txnFeeDetails.forEach((feeDetail, feeIndex) => {
                    flattened.push({
                        sNo: globalSNo,
                        paymentMode: group.paymentMode,
                        feeDetail: feeDetail,
                        groupIndex: groupIndex,
                        groupGlobalIndex: groupGlobalIndex,
                        feeIndex: feeIndex,
                        isFirstRowInGroup: groupGlobalIndex === 0,
                        isLastRowInGroup: groupGlobalIndex === totalRowsInGroup - 1,
                        isFirstRowInTransaction: feeIndex === 0,
                        isLastRowInTransaction: feeIndex === totalFeeDetails - 1,
                        transactionRowSpan: totalFeeDetails,
                        paymentModeRowSpan: totalRowsInGroup,
                        transactionId: feeDetail.transactionId,
                        receiptNo: receiptNo,
                        uniqueKey: `${globalSNo}-${receiptNo}-${feeIndex}`
                    });
                    groupGlobalIndex++;
                });
            });
            
            globalSNo++;
        });
        
        return flattened;
    };

    /**
     * Transform API response to table format
     * Group collections by paymentModeId, then by transaction
     */
    transformApiDataToTableFormat = (collections) => {
        if (!collections || collections.length === 0) {
            return [];
        }

        // Group collections by paymentModeId first
        const paymentModeMap = {};
        
        collections.forEach((collection) => {
            const paymentMode = collection.paymentMode || 'Unknown';
            const paymentModeId = collection.paymentModeId || 'unknown-id';
            
            // Use paymentModeId as the key to group properly
            if (!paymentModeMap[paymentModeId]) {
                paymentModeMap[paymentModeId] = {
                    paymentMode: paymentMode,
                    transactions: []
                };
            }

            // Each collection is a separate transaction
            const transactionId = collection.transctionId || '';
            const receiptNo = collection.receiptNo || 'N/A';
            const receiptDate = collection.receiptDate || null;
            const transactionDate = collection.transactionDate || null;
            const remarks = collection.remarks || null;
            const totalAmountPaid = collection.amountPaid || 0;

            const transactionFeeDetails = [];

            // Each feePaidDetails item becomes a row in the table
            if (collection.feePaidDetails && collection.feePaidDetails.length > 0) {
                collection.feePaidDetails.forEach((feeDetail) => {
                    transactionFeeDetails.push({
                        transactionId: transactionId,
                        remarks: remarks,
                        receiptNo: receiptNo,
                        receiptDate: receiptDate,
                        transactionDate: transactionDate,
                        feeType: `${feeDetail.feeType} - ${feeDetail.paymentTerm}`,
                        paidAmount: feeDetail.amountPaying || 0,
                        total: totalAmountPaid
                    });
                });
            }

            // Add transaction to the payment mode group
            paymentModeMap[paymentModeId].transactions.push({
                transactionId: transactionId,
                receiptNo: receiptNo,
                feeDetails: transactionFeeDetails
            });
        });

        // Convert map to array format and flatten
        const result = [];
        let sNoCounter = 1;
        
        Object.keys(paymentModeMap).forEach((paymentModeId) => {
            const allFeeDetails = [];
            
            // Flatten all transactions under this payment mode
            paymentModeMap[paymentModeId].transactions.forEach((transaction) => {
                allFeeDetails.push(...transaction.feeDetails);
            });

            result.push({
                sNo: sNoCounter++,
                paymentMode: paymentModeMap[paymentModeId].paymentMode,
                paymentModeId: paymentModeId,
                feeDetails: allFeeDetails
            });
        });

        console.log('Payment Mode Groups:', result);
        return result;
    };

    formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    calculateGrandTotal = () => {
        const { flattenedData } = this.state;
        return flattenedData.reduce((sum, row) => sum + (row.feeDetail.paidAmount || 0), 0);
    };

    // Column body templates
    snoBodyTemplate = (rowData) => {
        if (!rowData.isFirstRowInGroup) return '';
        return (
            <span style={{ display: 'block', textAlign: 'center' }}>
                {rowData.sNo}
            </span>
        );
    };

    paymentModeBodyTemplate = (rowData) => {
        if (!rowData.isFirstRowInGroup) return '';
        return <span>{rowData.paymentMode}</span>;
    };

    transactionIdBodyTemplate = (rowData) => {
        if (!rowData.isFirstRowInTransaction) return '';
        const txnId = rowData.feeDetail.transactionId || 'N/A';
        return (
            <div className="transaction-id-info">
                <div className="transaction-id">{txnId}</div>
                {rowData.feeDetail.remarks && (
                    <div className="remarks">Remarks : {rowData.feeDetail.remarks}</div>
                )}
            </div>
        );
    };

    receiptNoBodyTemplate = (rowData) => {
        if (!rowData.isFirstRowInTransaction) return '';
        return <span>{rowData.feeDetail.receiptNo}</span>;
    };

    receiptDateBodyTemplate = (rowData) => {
        if (!rowData.isFirstRowInTransaction) return '';
        return <span>{this.formatDate(rowData.feeDetail.receiptDate)}</span>;
    };

    feeTypeBodyTemplate = (rowData) => {
        return <span>{rowData.feeDetail.feeType}</span>;
    };

    paidAmountBodyTemplate = (rowData) => {
        return <span>{rowData.feeDetail.paidAmount.toFixed(2)}</span>;
    };

    totalBodyTemplate = (rowData) => {
        if (!rowData.isFirstRowInTransaction) return '';
        return <span>{rowData.feeDetail.total.toFixed(2)}</span>;
    };

    rowClassName = (rowData) => {
        const classes = [];
        
        // Add class for first row in transaction (to show separator from previous transaction)
        if (rowData.isFirstRowInTransaction) {
            classes.push('transaction-start-row');
        }
        
        // Add class for first row in payment mode group (no top border on very first row)
        if (rowData.isFirstRowInGroup) {
            classes.push('payment-mode-first-row');
        }
        
        // Add thick border on the last row of each payment mode group
        if (rowData.isLastRowInGroup) {
            classes.push('payment-mode-group-last-row');
        }
        
        return classes.join(' ');
    };

    render() {
        const { flattenedData, isLoading } = this.state;
        const grandTotal = this.calculateGrandTotal();

        return (
            <div className="payment-mode-table-container">
                <div className="info-note">
                    <i className="pi pi-info-circle"></i>
                    <span>Note : Transactions which are showing not including of Other fee Collections, School Store Transactions.</span>
                </div>

                {isLoading ? (
                    <div className="loading-container" style={{ textAlign: 'center', padding: '2rem' }}>
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                        <p>Loading transactions...</p>
                    </div>
                ) : (
                    <div className="payment-mode-table-wrapper">
                        <div ref={this.dataScrollRef} className="data-table-scroll-container">
                            <DataTable
                                value={flattenedData}
                                scrollable
                                scrollHeight="flex"
                                className="payment-mode-table"
                                showGridlines
                                rowClassName={this.rowClassName}
                                emptyMessage="No results found"
                            >
                                <Column
                                    header="S.No"
                                    body={this.snoBodyTemplate}
                                    headerClassName="sno-header"
                                    bodyClassName="sno-cell"
                                    style={{ width: '80px', minWidth: '80px' }}
                                />
                                <Column
                                    header="Payment Mode"
                                    body={this.paymentModeBodyTemplate}
                                    headerClassName="payment-mode-header"
                                    bodyClassName="payment-mode-cell"
                                    style={{ width: '150px', minWidth: '150px' }}
                                />
                                <Column
                                    header="TransactionID"
                                    body={this.transactionIdBodyTemplate}
                                    headerClassName="transaction-id-header"
                                    bodyClassName="transaction-id-cell"
                                    style={{ width: '250px', minWidth: '250px' }}
                                />
                                <Column
                                    header="Receipt No"
                                    body={this.receiptNoBodyTemplate}
                                    headerClassName="receipt-no-header"
                                    bodyClassName="receipt-no-cell"
                                    style={{ width: '150px', minWidth: '150px' }}
                                />
                                <Column
                                    header="Receipt Date"
                                    body={this.receiptDateBodyTemplate}
                                    headerClassName="receipt-date-header"
                                    bodyClassName="receipt-date-cell"
                                    style={{ width: '150px', minWidth: '150px' }}
                                />
                                <Column
                                    header="Fee Type"
                                    body={this.feeTypeBodyTemplate}
                                    headerClassName="fee-type-header"
                                    bodyClassName="fee-type-cell"
                                    style={{ width: '280px', minWidth: '280px' }}
                                />
                                <Column
                                    header="Paid Amount"
                                    body={this.paidAmountBodyTemplate}
                                    headerClassName="paid-amount-header"
                                    bodyClassName="paid-amount-cell"
                                    style={{ width: '150px', minWidth: '150px' }}
                                />
                                <Column
                                    header="Total"
                                    body={this.totalBodyTemplate}
                                    headerClassName="total-header"
                                    bodyClassName="total-cell"
                                    style={{ width: '150px', minWidth: '150px' }}
                                />
                            </DataTable>
                        </div>

                        {/* Grand Total Row */}
                        <div ref={this.grandTotalScrollRef}>
                            <DataTable
                                value={[{ total: grandTotal }]}
                                className="payment-mode-grand-total-table"
                                scrollable
                                scrollHeight="flex"
                                showGridlines
                            >
                                <Column body={() => ''} style={{ width: '80px', minWidth: '80px' }} />
                                <Column body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                <Column body={() => ''} style={{ width: '250px', minWidth: '250px' }} />
                                <Column body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                <Column body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                <Column body={() => ''} style={{ width: '280px', minWidth: '280px' }} />
                                <Column body={() => <strong style={{ color: '#076EFF' }}>Total</strong>} style={{ width: '150px', minWidth: '150px', textAlign: 'right', paddingRight: '1rem' }} />
                                <Column body={() => <strong style={{ color: '#076EFF' }}>{grandTotal.toFixed(2)}</strong>} style={{ width: '150px', minWidth: '150px', textAlign: 'left' }} />
                            </DataTable>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}
